import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import Shell from "../layout/Shell";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { EmptyState, ErrorBanner, LoadingCard, Pill } from "../lib/ui";

// -----------------------------------------------------------------------
// Create → Import video (Round 21, 2026-11-02)
//
// A finished MP4/MOV enters the SAME governed path generated reels use:
//   upload (server validates with ffprobe) → "Draft captions from Vault"
//   (approved claims only) → Guardian per channel → owner edits re-run
//   Guardian → owner approves → the set appears in Publish → New Publish.
// -----------------------------------------------------------------------

const CHANNEL_ORDER = ["facebook_reel", "instagram_reel", "youtube_short", "pinterest_video_pin"];
const CHANNEL_LABELS = {
  facebook_reel: "Facebook Reel",
  instagram_reel: "Instagram Reel",
  youtube_short: "YouTube Short",
  pinterest_video_pin: "Pinterest video pin",
};
const TITLED = new Set(["youtube_short", "pinterest_video_pin"]);

function fmtDate(v) {
  if (!v) return "\u2014";
  try {
    return new Date(v).toLocaleString(undefined, {
      year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit",
    });
  } catch { return String(v); }
}

function VerdictPill({ verdict, testId }) {
  if (verdict === "pass") return <Pill tone="success" testId={testId}>Guardian: pass</Pill>;
  if (verdict === "needs_review") return <Pill tone="warn" testId={testId}>Guardian: needs review</Pill>;
  if (verdict === "block") return <Pill tone="danger" testId={testId}>Guardian: blocked</Pill>;
  return <Pill tone="muted" testId={testId}>Not checked</Pill>;
}

function Findings({ findings, testId }) {
  if (!findings || findings.length === 0) return null;
  return (
    <ul data-testid={testId} style={{ margin: "8px 0 0", paddingLeft: 18, fontSize: 12.5, lineHeight: 1.5 }}>
      {findings.map((f, i) => (
        <li key={i} style={{ color: (f.severity || "").toLowerCase() === "block" ? "#8a2b0a" : "var(--bo-muted)" }}>
          <strong>{(f.severity || "info").toUpperCase()}</strong> · {f.layer || f.rule} — {f.message}
          {f.offender ? <> <span className="bo-mono">“{String(f.offender).slice(0, 100)}”</span></> : null}
        </li>
      ))}
    </ul>
  );
}

export default function ImportVideo() {
  const nav = useNavigate();
  const { role } = useAuth();
  const isOwner = role === "owner";

  const [products, setProducts] = useState([]);
  const [imports, setImports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // upload form
  const fileRef = useRef(null);
  const [productId, setProductId] = useState("");
  const [title, setTitle] = useState("");
  const [notes, setNotes] = useState("");
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);

  // selected import
  const [selectedId, setSelectedId] = useState("");
  const [detail, setDetail] = useState(null);
  const [drafting, setDrafting] = useState(false);
  const [draftWarning, setDraftWarning] = useState(null);
  const [edits, setEdits] = useState({});
  const [savingKey, setSavingKey] = useState("");
  const [approving, setApproving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [actionError, setActionError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [rp, ri] = await Promise.all([
        api.get("/products"),
        api.get("/creative/video-imports"),
      ]);
      setProducts(rp.data.products || []);
      setImports(ri.data.imports || []);
    } catch (e) {
      setError(formatApiError(e));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadDetail = useCallback(async (id) => {
    if (!id) { setDetail(null); return; }
    try {
      const r = await api.get(`/creative/video-imports/${id}`);
      setDetail(r.data);
      setEdits({});
    } catch (e) {
      setActionError(formatApiError(e));
    }
  }, []);

  useEffect(() => { loadDetail(selectedId); }, [selectedId, loadDetail]);

  const selectedImport = detail?.import || null;
  const approvedSet = detail?.approved_asset_set || null;
  const channelCaptions = selectedImport?.channel_captions || {};
  const blockedKeys = useMemo(
    () => CHANNEL_ORDER.filter((k) => channelCaptions[k] && channelCaptions[k].gate?.verdict === "block"),
    [channelCaptions],
  );
  const hasDrafts = Object.keys(channelCaptions).length > 0;
  const canApprove = isOwner && hasDrafts && blockedKeys.length === 0 && !approvedSet;

  async function handleUpload(e) {
    e.preventDefault();
    setUploadError(null); setFlash(null);
    const file = fileRef.current?.files?.[0];
    if (!file) { setUploadError("Choose an MP4 or MOV file first."); return; }
    if (!productId) { setUploadError("Pick the product this video is about."); return; }
    const fd = new FormData();
    fd.append("file", file, file.name);
    fd.append("product_id", productId);
    if (title.trim()) fd.append("title", title.trim());
    if (notes.trim()) fd.append("notes", notes.trim());
    setUploading(true);
    try {
      const r = await api.post("/creative/video-imports", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 180000,
      });
      const row = r.data.import;
      setFlash(`Imported ${row.source_filename} · ${row.probe.width}×${row.probe.height} · ${row.probe.duration_s.toFixed(1)} s · ${row.probe.codec}. Now draft captions from the Vault.`);
      setTitle(""); setNotes(""); setFileName("");
      if (fileRef.current) fileRef.current.value = "";
      await load();
      setSelectedId(row.id);
    } catch (err) {
      setUploadError(formatApiError(err));
    } finally { setUploading(false); }
  }

  async function handleDraft() {
    if (!selectedId) return;
    setDrafting(true); setActionError(null); setDraftWarning(null); setFlash(null);
    try {
      const r = await api.post(`/creative/video-imports/${selectedId}/draft-captions`, {}, { timeout: 120000 });
      setDraftWarning(r.data.warning || null);
      setFlash(r.data.draft_source === "model"
        ? "Captions drafted from the Vault and checked by Guardian."
        : "Deterministic captions drafted from the approved claims (model unavailable) and checked by Guardian.");
      await loadDetail(selectedId);
      await load();
    } catch (e) {
      setActionError(formatApiError(e));
    } finally { setDrafting(false); }
  }

  function editValue(key, field) {
    const e = edits[key] || {};
    if (field in e) return e[field];
    return (channelCaptions[key] || {})[field] || "";
  }

  async function handleSave(key) {
    setSavingKey(key); setActionError(null); setFlash(null);
    try {
      const body = TITLED.has(key)
        ? { title: editValue(key, "title"), description: editValue(key, "description") }
        : { caption: editValue(key, "caption") };
      await api.put(`/creative/video-imports/${selectedId}/captions/${key}`, body);
      setFlash(`${CHANNEL_LABELS[key]} caption saved and re-checked by Guardian.`);
      await loadDetail(selectedId);
    } catch (e) {
      setActionError(formatApiError(e));
    } finally { setSavingKey(""); }
  }

  async function handleApprove() {
    setApproving(true); setActionError(null); setFlash(null);
    try {
      const r = await api.post(`/creative/video-imports/${selectedId}/approve`, {});
      const set = r.data.approved_asset_set;
      setFlash(`Approved. The set “${set.name}” is now available in Publish → New Publish.`);
      await loadDetail(selectedId);
      await load();
    } catch (e) {
      setActionError(formatApiError(e));
    } finally { setApproving(false); }
  }

  return (
    <Shell title="Import video">
      <div className="bo-page" data-testid="import-video-page">
        <div style={{ marginBottom: 16 }}>
          <div className="bo-eyebrow">Create &middot; Governed import</div>
          <h1 style={{ fontFamily: "var(--bo-serif)", fontSize: 28, margin: "4px 0 4px 0" }}>Import a finished video</h1>
          <p style={{ color: "var(--bo-muted)", margin: 0, fontSize: 14, maxWidth: 760 }}>
            Bring a reel or short that was cut outside Brand OS into the governed path: the server
            checks the file, captions are drafted from the product&rsquo;s approved Vault claims only,
            every caption goes through Guardian, and the owner&rsquo;s approval makes it an approved
            reel set for Publish &rarr; New Publish. Nothing posts from this page.
          </p>
        </div>

        {error && <ErrorBanner error={error} testId="import-video-load-error" />}
        {flash && <div className="bo-flash" data-testid="import-video-flash" style={{ marginBottom: 12 }}>{flash}</div>}

        {/* ---- 1 · Upload ---- */}
        <section className="bo-card" data-testid="import-video-upload-card" style={{ padding: 16, marginBottom: 16 }}>
          <h2 className="bo-serif" style={{ fontSize: 18, margin: "0 0 10px" }}>1 &middot; Upload the file</h2>
          <form onSubmit={handleUpload} style={{ display: "grid", gap: 12, maxWidth: 720 }}>
            <div className="bo-field" style={{ margin: 0 }}>
              <label className="bo-label" htmlFor="import-video-file">Video file (MP4 or MOV · vertical 9:16, 720×1280 to 1080×1920 · 3–90 s · H.264 · up to 100 MB)</label>
              <input
                id="import-video-file"
                ref={fileRef}
                type="file"
                accept="video/mp4,video/quicktime,.mp4,.mov"
                data-testid="import-video-file-input"
                onChange={(e) => setFileName(e.target.files?.[0]?.name || "")}
                style={{ display: "block" }}
              />
              {fileName && <div className="bo-hint" data-testid="import-video-file-name">Selected: {fileName}</div>}
            </div>
            <div className="bo-field" style={{ margin: 0 }}>
              <label className="bo-label" htmlFor="import-video-product">Product (required)</label>
              <select
                id="import-video-product"
                className="bo-input"
                data-testid="import-video-product-select"
                value={productId}
                onChange={(e) => setProductId(e.target.value)}
                required
              >
                <option value="">Pick a product…</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            <div className="bo-field" style={{ margin: 0 }}>
              <label className="bo-label" htmlFor="import-video-title">Title</label>
              <input id="import-video-title" className="bo-input" data-testid="import-video-title-input"
                     value={title} onChange={(e) => setTitle(e.target.value)} placeholder="e.g. The Well · Just Show Me (Jessica v2)" maxLength={200} />
            </div>
            <div className="bo-field" style={{ margin: 0 }}>
              <label className="bo-label" htmlFor="import-video-notes">Notes (optional · context for the caption drafts, never posted)</label>
              <textarea id="import-video-notes" className="bo-input" data-testid="import-video-notes-input" rows={3}
                        value={notes} onChange={(e) => setNotes(e.target.value)} maxLength={2000}
                        placeholder="What the video shows, the end card, anything the caption writer should know." />
            </div>
            {uploadError && <ErrorBanner error={uploadError} testId="import-video-upload-error" />}
            <div>
              <button type="submit" className="bo-btn" data-testid="import-video-upload-btn" disabled={uploading}>
                {uploading ? "Checking and storing…" : "Upload and validate"}
              </button>
            </div>
          </form>
        </section>

        {/* ---- 2 · Imports list ---- */}
        <section className="bo-card" data-testid="import-video-list-card" style={{ padding: 16, marginBottom: 16 }}>
          <h2 className="bo-serif" style={{ fontSize: 18, margin: "0 0 10px" }}>2 &middot; Imported videos</h2>
          {loading ? <LoadingCard label="Loading imports…" /> : imports.length === 0 ? (
            <EmptyState testId="import-video-empty" title="No imported videos yet"
                        description="Upload a finished MP4 above. It will show here with its validation details." />
          ) : (
            <div style={{ display: "grid", gap: 8 }}>
              {imports.map((r) => (
                <label key={r.id} data-testid={`import-row-${r.id}`}
                       style={{ display: "flex", gap: 10, alignItems: "center", padding: "10px 12px", borderRadius: 8,
                                border: `1px solid ${selectedId === r.id ? "var(--bo-accent)" : "var(--bo-line)"}`,
                                background: selectedId === r.id ? "var(--bo-accent-soft)" : "var(--bo-panel)", cursor: "pointer" }}>
                  <input type="radio" name="import-row" checked={selectedId === r.id} onChange={() => setSelectedId(r.id)} />
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      <Pill tone="info">Imported</Pill>
                      <span style={{ fontFamily: "var(--bo-serif)", fontSize: 15 }}>{r.title || r.source_filename}</span>
                      {r.asset_set_frozen ? <Pill tone="success">Approved</Pill>
                        : r.status === "drafted" ? <Pill tone="warn">Captions drafted</Pill>
                        : <Pill tone="muted">Uploaded</Pill>}
                    </div>
                    <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 2 }}>
                      {r.product_name || "—"} · {r.source_filename} · {r.probe?.width}×{r.probe?.height} · {r.probe?.duration_s?.toFixed?.(1)} s · {r.probe?.codec} · {fmtDate(r.created_at)}
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}
        </section>

        {/* ---- 3 · Captions ---- */}
        {selectedImport && (
          <section className="bo-card" data-testid="import-video-captions-card" style={{ padding: 16, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 10 }}>
              <h2 className="bo-serif" style={{ fontSize: 18, margin: 0 }}>3 &middot; Captions, governed</h2>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" className="bo-btn" data-testid="import-video-draft-btn"
                        onClick={handleDraft} disabled={drafting || !!approvedSet}>
                  {drafting ? "Drafting and checking…" : hasDrafts ? "Re-draft captions from Vault" : "Draft captions from Vault"}
                </button>
                {isOwner && (
                  <button type="button" className="bo-btn" data-testid="import-video-approve-btn"
                          onClick={handleApprove} disabled={!canApprove || approving}
                          title={!hasDrafts ? "Draft captions first" : blockedKeys.length ? "Fix blocked captions first" : approvedSet ? "Already approved" : "Approve and freeze"}>
                    {approving ? "Approving…" : approvedSet ? "Approved" : "Approve set"}
                  </button>
                )}
              </div>
            </div>
            <p className="bo-hint" style={{ marginTop: 6 }}>
              Drafts use only the product&rsquo;s approved claims. Every caption is checked by Guardian
              (deterministic claim support + advisory review). A block must be fixed; it cannot be signed off.
              Editing a caption re-runs Guardian. {isOwner ? "Approving records you and the time." : "Only the owner can approve."}
            </p>
            <div style={{ fontSize: 12.5, color: "var(--bo-muted)", marginTop: 4 }} data-testid="import-video-selected-meta">
              {selectedImport.source_filename} · sha256 <span className="bo-mono">{(selectedImport.sha256 || "").slice(0, 16)}…</span>
              {selectedImport.landing_url && <> · CTA link <span className="bo-mono">{selectedImport.landing_url}</span></>}
              {selectedImport.draft_source && <> · drafts: {selectedImport.draft_source === "model" ? "model-written" : "deterministic fallback"}</>}
            </div>
            {draftWarning && <div className="bo-hint" data-testid="import-video-draft-warning" style={{ marginTop: 8, color: "#8a5a0a" }}>{draftWarning}</div>}
            {actionError && <div style={{ marginTop: 8 }}><ErrorBanner error={actionError} testId="import-video-action-error" /></div>}
            {approvedSet && (
              <div className="bo-flash" data-testid="import-video-approved-banner" style={{ marginTop: 10 }}>
                Approved by owner {fmtDate(approvedSet.approved_at)} · set <span className="bo-mono">{approvedSet.id.slice(0, 8)}…</span>.{" "}
                <button type="button" className="bo-button-secondary" data-testid="import-video-go-publish"
                        onClick={() => nav("/brand-os/publish/new")}>Open New Publish →</button>
              </div>
            )}

            {!hasDrafts ? (
              <div style={{ marginTop: 12 }}>
                <EmptyState testId="import-video-no-captions" title="No captions yet"
                            description="Click “Draft captions from Vault” to write one caption per channel from the approved claims." />
              </div>
            ) : (
              <div style={{ display: "grid", gap: 14, marginTop: 14 }}>
                {CHANNEL_ORDER.filter((k) => channelCaptions[k]).map((key) => {
                  const cap = channelCaptions[key];
                  const gate = cap.gate || {};
                  const dirty = !!edits[key];
                  return (
                    <div key={key} data-testid={`import-caption-${key}`}
                         style={{ border: "1px solid var(--bo-line)", borderRadius: 8, padding: 12 }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <strong>{CHANNEL_LABELS[key]}</strong>
                        <VerdictPill verdict={gate.verdict} testId={`import-caption-${key}-verdict`} />
                      </div>
                      {TITLED.has(key) ? (
                        <>
                          <label className="bo-label" style={{ marginTop: 8 }}>Title</label>
                          <input className="bo-input" data-testid={`import-caption-${key}-title`}
                                 value={editValue(key, "title")} disabled={!!approvedSet}
                                 onChange={(e) => setEdits({ ...edits, [key]: { ...(edits[key] || {}), title: e.target.value } })} />
                          <label className="bo-label" style={{ marginTop: 8 }}>Description</label>
                          <textarea className="bo-input" rows={5} data-testid={`import-caption-${key}-description`}
                                    value={editValue(key, "description")} disabled={!!approvedSet}
                                    onChange={(e) => setEdits({ ...edits, [key]: { ...(edits[key] || {}), description: e.target.value } })} />
                        </>
                      ) : (
                        <>
                          <label className="bo-label" style={{ marginTop: 8 }}>Caption</label>
                          <textarea className="bo-input" rows={6} data-testid={`import-caption-${key}-caption`}
                                    value={editValue(key, "caption")} disabled={!!approvedSet}
                                    onChange={(e) => setEdits({ ...edits, [key]: { ...(edits[key] || {}), caption: e.target.value } })} />
                        </>
                      )}
                      <div style={{ fontSize: 12, color: "var(--bo-muted)", marginTop: 6 }}>
                        Claims used: {(cap.claim_ids_used || []).length ? cap.claim_ids_used.join(", ") : "none"}
                      </div>
                      <Findings findings={gate.findings} testId={`import-caption-${key}-findings`} />
                      {!approvedSet && (
                        <div style={{ marginTop: 8 }}>
                          <button type="button" className="bo-button-secondary" data-testid={`import-caption-${key}-save`}
                                  onClick={() => handleSave(key)} disabled={!dirty || savingKey === key}>
                            {savingKey === key ? "Saving and re-checking…" : "Save and re-run Guardian"}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </section>
        )}
      </div>
    </Shell>
  );
}
